import { clientLogger } from '@/lib/clientLogger';
/**
 * Push Notification Service
 * 
 * Manages FCM push notifications for native (Android/iOS) platforms.
 * Uses data-only FCM payloads and constructs local notifications client-side
 * for full control over notification display in all app states.
 */

import { PushNotifications } from '@capacitor/push-notifications';
import { LocalNotifications } from '@capacitor/local-notifications';
import { isNative, isAndroid, getPlatform, requestPermissionExclusive } from './capacitor';
import { apiService } from './api';
import { hasNavigationHistory, updateAppHistory } from './navigation';
import { NOTIFICATIONS_CHANGED_EVENT, openNotificationTarget } from './notificationCenter';

let currentToken: string | null = null;
let initialized = false;

/**
 * Initialize push notifications. Call after user is authenticated.
 * Requests permission, registers for push, and sends the FCM token to the backend.
 */
export async function initializePushNotifications(): Promise<void> {
  if (!isNative() || initialized) return;

  try {
    const permResult = await requestPermissionExclusive(() => PushNotifications.requestPermissions());
    if (permResult.receive !== 'granted') {
      clientLogger.info('[Push] Permission not granted');
      return;
    }

    // Create a high-importance notification channel for push notifications.
    // This separates them from the location-tracking channel and enables
    // sound, vibration, and heads-up display.
    // Android only: createChannel() rejects with unimplemented() on iOS, which
    // would abort init before register() below ever runs.
    if (isAndroid()) {
      await LocalNotifications.createChannel({
        id: 'dispatch_push',
        name: 'Dispatch Notifications',
        description: 'Job assignments, messages, and other alerts',
        importance: 5, // max importance = heads-up + sound + vibration
        visibility: 1, // public
        vibration: true,
        sound: 'default',
      });
    }

    // Listen for registration success
    PushNotifications.addListener('registration', async (token) => {
      clientLogger.info('[Push] Registered with token:', token.value.substring(0, 20) + '...');
      currentToken = token.value;

      try {
        await apiService.registerFcmToken(token.value, getPlatform());
        clientLogger.info('[Push] Token registered with backend');
      } catch (error) {
        clientLogger.debug('[Push] Failed to register token with backend:', error);
      }
    });

    // Listen for registration errors
    PushNotifications.addListener('registrationError', (error) => {
      clientLogger.debug('[Push] Registration error:', error);
    });

    // Handle incoming data messages (app in foreground or background)
    PushNotifications.addListener('pushNotificationReceived', async (notification) => {
      clientLogger.info('[Push] Data message received:', notification);

      const data = notification.data || {};
      const eventName = data.event_name || '';

      // LocationRefreshRequested never reaches here on either platform — Android's
      // LocationRefreshMessagingService returns before delegating, and on iOS a
      // content-available push never hits Capacitor's willPresent. Both handle it
      // natively (LocationFix.java / LocationFix in AppDelegate.swift).

      const title = data.title || 'Dispatch';
      const body = data.body || '';

      // Show a local notification so the user sees it
      await LocalNotifications.schedule({
        notifications: [
          {
            id: Date.now(),
            title,
            body,
            channelId: 'dispatch_push',
            extra: { event_name: eventName, payload: data.payload },
          },
        ],
      });
    });

    // Handle notification tap (user interacted with notification)
    PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
      clientLogger.info('[Push] Notification tapped:', action);
      handleNotificationTap(action.notification.data);
    });

    // Handle local notification tap
    LocalNotifications.addListener('localNotificationActionPerformed', (action) => {
      clientLogger.info('[Push] Local notification tapped:', action);
      handleNotificationTap(action.notification.extra);
    });

    // Register with FCM
    await PushNotifications.register();
    initialized = true;
    clientLogger.info('[Push] Push notifications initialized');
  } catch (error) {
    clientLogger.debug('[Push] Failed to initialize push notifications:', error);
  }
}

/**
 * Handle navigation when a user taps a notification.
 */
function handleNotificationTap(data: Record<string, any> | undefined): void {
  if (!data) return;

  const eventName = data.event_name;
  let payload: Record<string, string> | undefined;

  try {
    payload = typeof data.payload === 'string'
      ? JSON.parse(data.payload)
      : data.payload;
  } catch {
    payload = undefined;
  }

  // Pushes backed by a notification-center entry carry its id; the tap counts as reading it.
  const notificationId = payload?.notification_id;
  if (notificationId) {
    apiService.markNotificationRead(notificationId)
      .then(() => window.dispatchEvent(new Event(NOTIFICATIONS_CHANGED_EVENT)))
      .catch((error) => clientLogger.debug('[Push] Failed to mark notification read:', error));
  }

  if (eventName === 'NewSms') {
    // A notification tap is an entry point, not a navigation, so back must lead somewhere
    // other than this same screen. Seed /jobs underneath when either is true:
    //   - nothing has been pushed yet (cold launch) — back would pop out of the SPA and
    //     Android would minimize the app;
    //   - we are already on /messaging — the pushed entry collapses back to /messaging once
    //     the conversation_id is consumed, so back would pop to an identical entry and
    //     appear to do nothing.
    // Otherwise the user has a real previous screen; leave their history alone.
    if (!hasNavigationHistory() || window.location.pathname === '/messaging') {
      updateAppHistory('/jobs', 'replace');
    }
  }

  // Job types open the job modal over the current page; message types navigate to messaging.
  openNotificationTarget(eventName, payload);
}

/**
 * Unregister push notifications. Call on logout (and whenever auth drops to signed-out).
 */
export async function unregisterPushNotifications(): Promise<void> {
  if (!isNative()) return;

  // Best effort: needs a live session, so it fails when offline, after the
  // session expired, or when auth was cleared without signOut().
  if (currentToken) {
    try {
      await apiService.unregisterFcmToken(currentToken);
      clientLogger.info('[Push] Token unregistered from backend');
    } catch (error) {
      clientLogger.warn('[Push] Failed to unregister token from backend:', error);
    }
    currentToken = null;
  }

  // The backstop: delete the token on the device itself (Android: FirebaseMessaging.deleteToken,
  // iOS: unregisterForRemoteNotifications), so a backend row that survived the call above can no
  // longer reach this device. register() on the next login re-enables and issues a fresh token.
  try {
    await PushNotifications.unregister();
    await PushNotifications.removeAllListeners();
  } catch (error) {
    clientLogger.warn('[Push] Failed to clean up push notifications:', error);
  }
  initialized = false;
}
